// src/screens/MerchOrderHistoryScreen.js
// ============================================================================
// MERCH ORDER HISTORY SCREEN
//
// Accessible from Profile → Order History. Lists the signed-in user's merch
// purchases newest first, with status, ship-to city/state, and (when the
// Printful webhook has fired) carrier + tracking link.
//
// Data: getMerchOrderHistory Cloud Function returns server-trimmed records —
// the full street address stays on the server; we display only city/state/zip.
//
// Status states the screen handles (server-side enum in stripeMerch.js):
//   - pending / paid / submitted_to_printful → processing (yellow)
//   - shipped → green, surface tracking
//   - payment_received_fulfillment_failed / printful_failed /
//     printful_canceled / returned / failed_checkout_creation → red,
//     surface a support note
// ============================================================================

import React, { useEffect, useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  Linking,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { onAuthStateChanged } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '../../firebaseConfig';
import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';

// ----------------------------------------------------------------------------
// Status interpretation
// ----------------------------------------------------------------------------

const STATUS_DISPLAY = {
  pending:                            { label: 'Awaiting Payment',  tone: 'processing' },
  paid:                               { label: 'Payment Received',  tone: 'processing' },
  submitted_to_printful:              { label: 'In Print Queue',    tone: 'processing' },
  shipped:                            { label: 'Shipped',           tone: 'shipped' },
  returned:                           { label: 'Returned',          tone: 'failed' },
  printful_failed:                    { label: 'Order Failed',      tone: 'failed' },
  printful_canceled:                  { label: 'Order Canceled',    tone: 'failed' },
  printful_status_changed:            { label: 'Status Update',     tone: 'processing' },
  payment_received_fulfillment_failed:{ label: 'Needs Attention',   tone: 'failed' },
  failed_checkout_creation:           { label: 'Checkout Failed',   tone: 'failed' },
};

function getStatusDisplay(status) {
  return STATUS_DISPLAY[status] || { label: status || 'Unknown', tone: 'processing' };
}

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

function formatCurrency(cents) {
  if (typeof cents !== 'number') return '$0.00';
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDate(isoString) {
  if (!isoString) return '';
  try {
    return new Date(isoString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch (_e) {
    return '';
  }
}

function shortOrderId(orderId) {
  if (!orderId) return '—';
  return orderId.slice(-8).toUpperCase();
}

// Build a human-readable date range from Printful's ISO date strings
// ("YYYY-MM-DD"). Examples:
//   formatDeliveryWindow('2026-06-05', '2026-06-08') → 'Jun 5–8, 2026'
//   formatDeliveryWindow('2026-06-28', '2026-07-02') → 'Jun 28 – Jul 2, 2026'
//   formatDeliveryWindow('2026-06-05', '2026-06-05') → 'Jun 5, 2026'
//   formatDeliveryWindow('2026-06-05', null)        → 'Jun 5, 2026'
// Parses with explicit T00:00 so JS doesn't apply timezone offsets that
// could shift the date by a day in negative UTC offsets.
function formatDeliveryWindow(minIso, maxIso) {
  const parse = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(`${iso}T00:00:00`);
      return isNaN(d.getTime()) ? null : d;
    } catch (_e) {
      return null;
    }
  };
  const min = parse(minIso);
  const max = parse(maxIso);
  if (!min && !max) return '';

  const monthFmt = { month: 'short' };
  const yearFmt = { year: 'numeric' };

  if (min && max && min.getTime() !== max.getTime()) {
    const minMonth = min.toLocaleDateString('en-US', monthFmt);
    const maxMonth = max.toLocaleDateString('en-US', monthFmt);
    const year = max.toLocaleDateString('en-US', yearFmt);
    if (minMonth === maxMonth) {
      return `${minMonth} ${min.getDate()}–${max.getDate()}, ${year}`;
    }
    return `${minMonth} ${min.getDate()} – ${maxMonth} ${max.getDate()}, ${year}`;
  }

  const only = min || max;
  return only.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function openLink(url) {
  if (!url) return;
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.open(url, '_blank', 'noopener,noreferrer');
  } else {
    Linking.openURL(url).catch(() => {});
  }
}

// ----------------------------------------------------------------------------
// Order card
// ----------------------------------------------------------------------------

function OrderCard({ order }) {
  const { label: statusLabel, tone } = getStatusDisplay(order.status);
  const ship = order.shipTo || {};
  const cityLine = [ship.city, ship.state].filter(Boolean).join(', ');
  const cityZip = ship.zip ? `${cityLine} ${ship.zip}` : cityLine;
  const deliveryWindow = formatDeliveryWindow(order.estimatedDeliveryMin, order.estimatedDeliveryMax);
  // Hide the estimate once the package is in motion or done — the actual
  // shipped date + tracking are the more relevant signals at that point.
  const showDeliveryEstimate = !!deliveryWindow && order.status !== 'shipped' && order.status !== 'returned';

  return (
    <View style={styles.card}>
      {/* Header row: product + status badge. Multi-item orders show a
          line-count summary instead of the single-product size + qty. */}
      <View style={styles.cardHeader}>
        <View style={{ flex: 1 }}>
          <Text style={styles.productName}>{order.productName || 'Order'}</Text>
          <Text style={styles.subline}>
            {Array.isArray(order.lineItems) && order.lineItems.length > 1
              ? `${order.lineItems.length} items · Qty ${order.itemCount || order.quantity}`
              : `${order.size ? `Size ${order.size} · ` : ''}Qty ${order.quantity}`}
          </Text>
        </View>
        <View
          style={[
            styles.statusBadge,
            tone === 'shipped' && styles.statusBadgeShipped,
            tone === 'failed' && styles.statusBadgeFailed,
          ]}
        >
          <Text
            style={[
              styles.statusBadgeText,
              tone === 'shipped' && styles.statusBadgeTextShipped,
              tone === 'failed' && styles.statusBadgeTextFailed,
            ]}
          >
            {statusLabel}
          </Text>
        </View>
      </View>

      {/* Detail rows */}
      <View style={styles.detailGrid}>
        <View style={styles.detailRow}>
          <Text style={styles.detailLabel}>Order #</Text>
          <Text style={styles.detailValueMono}>{shortOrderId(order.orderId)}</Text>
        </View>
        <View style={styles.detailRow}>
          <Text style={styles.detailLabel}>Placed</Text>
          <Text style={styles.detailValue}>{formatDate(order.createdAt)}</Text>
        </View>
        {showDeliveryEstimate ? (
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Est. delivery</Text>
            <Text style={[styles.detailValue, styles.detailValueEmphasis]}>{deliveryWindow}</Text>
          </View>
        ) : null}
        <View style={styles.detailRow}>
          <Text style={styles.detailLabel}>Total</Text>
          <Text style={styles.detailValue}>{formatCurrency(order.totalCents)}</Text>
        </View>
        {cityZip ? (
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Ship to</Text>
            <Text style={styles.detailValue}>{cityZip}</Text>
          </View>
        ) : null}
        {order.shippedAt ? (
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Shipped</Text>
            <Text style={styles.detailValue}>{formatDate(order.shippedAt)}</Text>
          </View>
        ) : null}
      </View>

      {/* Tracking — only when shipped */}
      {order.status === 'shipped' && order.trackingNumber ? (
        <View style={styles.trackingBlock}>
          <View style={{ flex: 1 }}>
            <Text style={styles.trackingLabel}>
              {order.carrier ? `${order.carrier} tracking` : 'Tracking number'}
            </Text>
            <Text style={styles.trackingNumber} selectable>
              {order.trackingNumber}
            </Text>
          </View>
          {order.trackingUrl ? (
            <TouchableOpacity
              style={styles.trackButton}
              onPress={() => openLink(order.trackingUrl)}
            >
              <Ionicons name="open-outline" size={16} color="#0A0A0A" />
              <Text style={styles.trackButtonText}>Track</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      ) : null}

      {/* Shipped but no tracking yet (rare race) */}
      {order.status === 'shipped' && !order.trackingNumber ? (
        <Text style={styles.helperNote}>
          Carrier hasn't scanned the package yet. Check back in a few hours.
        </Text>
      ) : null}

      {/* Failure note */}
      {tone === 'failed' ? (
        <View style={styles.failureBlock}>
          <Ionicons name="alert-circle" size={16} color="#FF6B6B" />
          <Text style={styles.failureText}>
            {order.failureReason
              ? `${order.failureReason}. We're on it — email support@critcash.com with order # ${shortOrderId(order.orderId)} if you'd like an update.`
              : `We hit a snag with this order. Email support@critcash.com with order # ${shortOrderId(order.orderId)} and we'll sort it out.`}
          </Text>
        </View>
      ) : null}
    </View>
  );
}

// ----------------------------------------------------------------------------
// Main screen
// ----------------------------------------------------------------------------

export default function MerchOrderHistoryScreen({ navigation }) {
  const [authReady, setAuthReady] = useState(false);
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthReady(true);
    });
    return unsub;
  }, []);

  const fetchOrders = useCallback(async () => {
    if (!user) {
      setOrders([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const functions = getFunctions();
      const getHistory = httpsCallable(functions, 'getMerchOrderHistory');
      const result = await getHistory({ limit: 50 });
      setOrders(result.data?.orders || []);
    } catch (err) {
      console.error('Failed to load order history:', err);
      setError(err?.message || 'Could not load your orders.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (authReady) fetchOrders();
  }, [authReady, fetchOrders]);

  return (
    <SafeAreaView style={styles.container}>
      <Header navigation={navigation} />
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.titleBlock}>
          <Text style={styles.title}>Your Orders</Text>
          <Text style={styles.subtitle}>
            Merch purchases, fulfillment status, and shipping tracking.
          </Text>
        </View>

        {!authReady || loading ? (
          <View style={styles.stateBlock}>
            <ActivityIndicator color="#FCD000" size="large" />
            <Text style={styles.stateText}>Loading your orders…</Text>
          </View>
        ) : !user ? (
          <View style={styles.stateBlock}>
            <Ionicons name="lock-closed" size={36} color="#666" />
            <Text style={styles.stateTitle}>Sign in to see your orders</Text>
            <TouchableOpacity
              style={styles.primaryButton}
              onPress={() => navigation.navigate('Login')}
            >
              <Text style={styles.primaryButtonText}>Go to Login</Text>
            </TouchableOpacity>
          </View>
        ) : error ? (
          <View style={styles.stateBlock}>
            <Ionicons name="alert-circle" size={36} color="#FF6B6B" />
            <Text style={styles.stateTitle}>Something went wrong</Text>
            <Text style={styles.stateText}>{error}</Text>
            <TouchableOpacity style={styles.primaryButton} onPress={fetchOrders}>
              <Text style={styles.primaryButtonText}>Try again</Text>
            </TouchableOpacity>
          </View>
        ) : orders.length === 0 ? (
          <View style={styles.stateBlock}>
            <Ionicons name="cube-outline" size={48} color="#666" />
            <Text style={styles.stateTitle}>No orders yet</Text>
            <Text style={styles.stateText}>
              Your CritCash merch purchases will appear here.
            </Text>
            <TouchableOpacity
              style={styles.primaryButton}
              onPress={() => navigation.navigate('Store')}
            >
              <Text style={styles.primaryButtonText}>Visit the Store</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <>
            {orders.map((order) => (
              <OrderCard key={order.orderId} order={order} />
            ))}
            <Text style={styles.footnote}>
              Need help with an order? Email{' '}
              <Text
                style={styles.link}
                onPress={() => openLink('mailto:support@critcash.com')}
              >
                support@critcash.com
              </Text>
              .
            </Text>
          </>
        )}

        <View style={{ height: 100 }} />
      </ScrollView>

      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );
}

// ============================================================================
// STYLES
// ============================================================================

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#0A0A0A' },
  scrollContent: { paddingHorizontal: 16, paddingTop: 16 },

  titleBlock: { marginBottom: 20 },
  title: { color: '#FFF', fontSize: 26, fontWeight: '800', marginBottom: 4 },
  subtitle: { color: '#999', fontSize: 14 },

  // States
  stateBlock: {
    alignItems: 'center',
    paddingVertical: 60,
    paddingHorizontal: 20,
    gap: 12,
  },
  stateTitle: { color: '#FFF', fontSize: 18, fontWeight: '700', marginTop: 8 },
  stateText: { color: '#888', fontSize: 14, textAlign: 'center', maxWidth: 420 },
  primaryButton: {
    backgroundColor: '#FCD000',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    marginTop: 8,
  },
  primaryButtonText: { color: '#0A0A0A', fontSize: 15, fontWeight: '800' },

  // Card
  card: {
    backgroundColor: '#1A1A1A',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#222',
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 12,
  },
  productName: { color: '#FFF', fontSize: 16, fontWeight: '700', marginBottom: 2 },
  subline: { color: '#888', fontSize: 13 },

  statusBadge: {
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 999,
    backgroundColor: '#3A2F00',
  },
  statusBadgeShipped: { backgroundColor: '#063D24' },
  statusBadgeFailed: { backgroundColor: '#3A0E0E' },
  statusBadgeText: {
    color: '#FCD000',
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  statusBadgeTextShipped: { color: '#2ECC71' },
  statusBadgeTextFailed: { color: '#FF6B6B' },

  // Detail grid
  detailGrid: { gap: 6, marginBottom: 8 },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  detailLabel: { color: '#777', fontSize: 13 },
  detailValue: { color: '#DDD', fontSize: 13, fontWeight: '600' },
  detailValueEmphasis: { color: '#FCD000' },
  detailValueMono: {
    color: '#DDD',
    fontSize: 13,
    fontWeight: '600',
    fontFamily: Platform.OS === 'web' ? 'Courier New, monospace' : 'Courier',
  },

  // Tracking
  trackingBlock: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#0E0E0E',
    borderWidth: 1,
    borderColor: '#FCD00040',
    borderRadius: 10,
    padding: 12,
    marginTop: 10,
    gap: 12,
  },
  trackingLabel: { color: '#888', fontSize: 11, marginBottom: 4, letterSpacing: 0.5 },
  trackingNumber: {
    color: '#FCD000',
    fontSize: 15,
    fontWeight: '700',
    fontFamily: Platform.OS === 'web' ? 'Courier New, monospace' : 'Courier',
  },
  trackButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#FCD000',
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 8,
  },
  trackButtonText: { color: '#0A0A0A', fontSize: 13, fontWeight: '800' },

  helperNote: {
    color: '#888',
    fontSize: 12,
    fontStyle: 'italic',
    marginTop: 10,
  },

  // Failure
  failureBlock: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    backgroundColor: '#1F0A0A',
    borderRadius: 8,
    padding: 12,
    marginTop: 10,
  },
  failureText: { flex: 1, color: '#FF9999', fontSize: 12, lineHeight: 18 },

  footnote: {
    color: '#666',
    fontSize: 12,
    textAlign: 'center',
    marginTop: 16,
  },
  link: { color: '#FCD000', textDecorationLine: 'underline' },
});
